import React, { useCallback, useMemo, useState } from 'react';
import { IPolicy, IEmployer, IUser, IPolicyScan, IPolicyProvider } from 'types';
import Spinner from 'components/spinner';
import Header from './PolicyListHeader';
import PolicyListItem from './PolicyListItem';
import styles from './policiesList.scss';
import ConfirmAction from 'components/confirm-action';
import { getActionHeaderAndContent } from 'helpers';
import AccordionList from 'components/accordion-list';
import LifeCoversList from './LifeCoversList';
import ActionsMenu from './ActionMenu';
import { PolicyStatus } from 'constants/policy';

interface IPoliciesListProps {
  policies: IPolicy[];
  policyScans?: IPolicyScan[];
  user?: IUser;
  onClick: (policy: IPolicy) => void;
  isOwner: boolean;
  id?: string;
  itemClassName?: string | ((policy: IPolicy) => string);
  loading?: boolean;
  showContent?: boolean;
  hideHeader?: boolean;
  actionMenu?: any;
  placeholder?: string | React.ReactElement;
  employers?: IEmployer[];
  policyProviders?: IPolicyProvider[];
  placeholderClassName?: string;
  headers?: string[];
  headerClassName?: string;
  showUpdateNow?: boolean;
  deletePolicy?: (id: string) => void;
}

const Placeholder = ({ className, placeholder }) => (
  <div className={classnames(styles.placeholder, className)}>{placeholder}</div>
);

const AccordionTitle = ({ index, item, toggleExpand, ...props }) => {
  return (
    <PolicyListItem
      {...props}
      id={`${props.id}_${index}`}
      policy={item}
      toggleExpand={toggleExpand}
      enableTitleClick={true}
    />
  );
};

const AccordionContent = ({ index, item, ...props }) => {
  if (item.employerId) {
    const handleSelect = () => props.onClick(item);
    return (
      <div
        className={styles.employerContent}
        id={`${props.id}_${index}_Content`}
        onClick={handleSelect}
      >
        <LifeCoversList policy={item} />
        <ActionsMenu
          id={item.id}
          setDeleted={props.setDeleteId}
          viewPolicy={handleSelect}
          isOwner={props.isOwner}
          showDelete={true}
          viewBtnText={
            item.status === PolicyStatus.FINISH_SETUP
              ? 'Setup'
              : 'View workplace'
          }
        />
      </div>
    );
  }

  if (item.data === 'bundle' || item.status === PolicyStatus.PROCESSING) {
    return null;
  }
  return (
    <PolicyListItem
      policy={item}
      {...props}
      id={`${props.id}_${index}_Content`}
      enableTitleClick={true}
    />
  );
};

const renderPolicies = (props: IPoliciesListProps, setDeleteId) => {
  const {
    id,
    isOwner,
    policies,
    onClick,
    actionMenu,
    itemClassName,
    policyProviders,
    placeholder,
    employers = [],
    user,
    placeholderClassName,
    showContent,
    showUpdateNow,
  } = props;

  if (policies.length) {
    return (
      <AccordionList
        id={props.id}
        className={classnames(
          styles.accordion,
          showContent && styles.showContent,
        )}
        items={policies}
        componentProps={{
          onClick,
          setDeleteId,
          isOwner,
          actionMenu,
          className: itemClassName,
          employers,
          policyProviders,
          user,
          id,
          showUpdateNow,
        }}
        titleComponent={AccordionTitle}
        contentComponent={AccordionContent}
        titleIcon={false}
      />
    );
  }

  return (
    <Placeholder placeholder={placeholder} className={placeholderClassName} />
  );
};

const PolicyList = (props: IPoliciesListProps) => {
  const {
    loading,
    deletePolicy,
    policyScans,
    headers,
    headerClassName,
  } = props;
  const [deleteId, setDeleteId] = useState(null);

  const handleConfirmCancel = useCallback(() => setDeleteId(null), []);

  const handleConfirm = useCallback(() => {
    deletePolicy(deleteId);
    setDeleteId(null);
  }, [deletePolicy, deleteId]);

  const policies = useMemo(
    () => !loading && renderPolicies(props, setDeleteId),
    [props, deletePolicy],
  );

  const { header, content } = getActionHeaderAndContent(policyScans, deleteId);

  return (
    <div className={styles.container}>
      <Header headers={headers} headerClassName={headerClassName} />
      {loading && (
        <div className={styles.loading}>
          <Spinner loading={loading} />
        </div>
      )}
      {policies}
      <ConfirmAction
        open={!!deleteId}
        header={header}
        content={content}
        cancelText="Back"
        confirmText="Delete"
        onCancel={handleConfirmCancel}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

PolicyList.defaultProps = {
  compact: true,
  placeholder: "You haven't added any of your Policies yet.",
};

export default React.memo(PolicyList);
